/**
 * Anruf dokumentieren.
 *
 * Der einzige Weg, ein Angebot zu pausieren: Ergebnis „Wiedervorlage" verlangt
 * ein Pflichtdatum, danach ruht der Vorgang bis zu diesem Tag.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { jetztUhrzeit } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';

type Ergebnis = 'beobachten' | 'wiedervorlage' | 'absage' | 'sonstiges';

export function AnrufModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const meta = useMeta();

  const [datum, setDatum] = useState(meta.heute);
  const [zeit, setZeit] = useState(jetztUhrzeit());
  const [notiz, setNotiz] = useState('');
  const [ergebnis, setErgebnis] = useState<Ergebnis>('beobachten');
  const [wiedervorlage, setWiedervorlage] = useState('');
  const [sonst, setSonst] = useState('');
  const [fehler, setFehler] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/anruf`, {
        datum,
        zeit,
        notiz: notiz.trim(),
        ergebnis,
        wiedervorlage: ergebnis === 'wiedervorlage' ? wiedervorlage : null,
        sonst: ergebnis === 'sonstiges' ? sonst.trim() : '',
      }),
    onSuccess: (antwort) => {
      onFertig();
      if (antwort.toast) toastZeigen(antwort.toast);
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  function speichern() {
    if (!notiz.trim()) {
      setFehler('Bitte eine kurze Gesprächsnotiz eintragen.');
      return;
    }
    if (ergebnis === 'wiedervorlage' && !wiedervorlage) {
      setFehler('Wiedervorlage-Datum ist Pflicht.');
      return;
    }
    if (ergebnis === 'sonstiges' && !sonst.trim()) {
      setFehler('Bitte das Ergebnis in eigenen Worten beschreiben.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      titel="Anruf dokumentieren"
      untertitel={`${angebot.name} · ${angebot.kunde.name} · ${angebot.kunde.telefon || 'keine Nummer hinterlegt'}`}
      breite={520}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={speichern} disabled={mutation.isPending}>
            {mutation.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </KnopfPrimaer>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label className="modal-feld" style={{ flex: 1, minWidth: 150 }}>
          <span>Datum</span>
          <input
            type="date"
            className="modal-eingabe"
            value={datum}
            onChange={(e) => {
              setDatum(e.target.value);
              setFehler('');
            }}
          />
        </label>
        <label className="modal-feld" style={{ width: 130 }}>
          <span>Uhrzeit</span>
          <input
            type="time"
            className="modal-eingabe"
            value={zeit}
            onChange={(e) => {
              setZeit(e.target.value);
              setFehler('');
            }}
          />
        </label>
      </div>

      <label className="modal-feld">
        <span>Gesprächsnotiz</span>
        <textarea
          className="modal-textarea"
          rows={4}
          placeholder="Was wurde besprochen?"
          value={notiz}
          onChange={(e) => {
            setNotiz(e.target.value);
            setFehler('');
          }}
        />
      </label>

      <div className="modal-feld">
        <span>Ergebnis</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {meta.anrufErgebnisse.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`modal-radio ${ergebnis === o.key ? 'aktiv' : ''}`}
              onClick={() => {
                setErgebnis(o.key as Ergebnis);
                setFehler('');
              }}
            >
              <span className="modal-radio-punkt" />
              <span className="modal-radio-text">{o.label}</span>
            </button>
          ))}
        </div>
      </div>

      {ergebnis === 'wiedervorlage' && (
        <label className="modal-feld">
          <span>Wiedervorlage am · Pflichtfeld</span>
          <input
            type="date"
            className={`modal-eingabe ${fehler && !wiedervorlage ? 'modal-eingabe--fehler' : ''}`}
            value={wiedervorlage}
            min={meta.heute}
            onChange={(e) => {
              setWiedervorlage(e.target.value);
              setFehler('');
            }}
          />
          <span className="modal-hinweis">
            Das Angebot ruht bis dahin – kein Nachfassen, keine Erinnerung. Am Wiedervorlage-Tag
            erscheint es automatisch wieder auf deinem Tisch.
          </span>
        </label>
      )}

      {ergebnis === 'sonstiges' && (
        <label className="modal-feld">
          <span>Ergebnis in eigenen Worten</span>
          <input
            className="modal-eingabe"
            placeholder="z. B. Angebot wird an Architekten weitergeleitet"
            value={sonst}
            onChange={(e) => {
              setSonst(e.target.value);
              setFehler('');
            }}
          />
        </label>
      )}

      {ergebnis === 'absage' && (
        <div className="modal-hinweis">
          Das Angebot wird auf „Abgesagt" gesetzt und verschwindet aus dem Cockpit.
        </div>
      )}

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
